import { addMinutes, isSameDay, endOfDay, isPast } from "date-fns";
import {
  toZonedTime,
  getTimezoneOffset,
  fromZonedTime,
  formatInTimeZone,
} from "date-fns-tz";
import { enUS } from "date-fns/locale/en-US";
import { fr } from "date-fns/locale/fr";
import { de } from "date-fns/locale/de";

const DEFAULT_TIMEZONE = "Europe/Zurich";

export function formatDateWithLocale(
  date: Date | string,
  formatStr: string,
  language: string = "fr",
  timezone: string = DEFAULT_TIMEZONE
) {
  // Get the correct locale
  let locale;
  switch (language) {
    case "en":
      locale = enUS;
      break;
    case "de":
      locale = de;
      break;
    case "fr":
    default:
      locale = fr;
      break;
  }

  // Ensure we have a Date object
  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Format with the specified locale
  return formatInTimeZone(dateObj, timezone, formatStr, { locale });
}

export function formatInTz(date: Date, formatStr: string, timezone?: string) {
  const tz = timezone || DEFAULT_TIMEZONE;
  return formatInTimeZone(date, tz, formatStr);
}

export function dateToLocalTime(date: Date, timezone?: string) {
  const tz = timezone || DEFAULT_TIMEZONE;
  return toZonedTime(date, tz);
}

export function localTimeToUtc(date: Date, timezone?: string) {
  const tz = timezone || DEFAULT_TIMEZONE;
  return fromZonedTime(date, tz);
}

export function getTzOffset(timezone?: string) {
  const tz = timezone || DEFAULT_TIMEZONE;
  return getTimezoneOffset(tz);
}

// Fixed helper for generating events with correct timezone
export function generateEventTimes(
  day: Date,
  timeSlot: string,
  durationMinutes: number,
  timezone?: string
) {
  const tz = timezone || DEFAULT_TIMEZONE;
  const [hours, minutes] = timeSlot.split(":").map(Number);

  // Get the date components
  const year = day.getFullYear();
  const month = day.getMonth();
  const dayOfMonth = day.getDate();

  // Create a UTC date with the desired local time
  const utcDate = new Date(
    Date.UTC(year, month, dayOfMonth, hours, minutes, 0)
  );

  // Convert this "local time as UTC" back to the actual UTC time
  // We need to reverse the timezone conversion
  const tempZoned = toZonedTime(utcDate, tz);
  const offsetMs = tempZoned.getTime() - utcDate.getTime();

  // Create the correct UTC time by subtracting the offset
  const startTime = new Date(utcDate.getTime() - offsetMs);

  const endTime = addMinutes(startTime, durationMinutes);

  return {
    startTime,
    endTime,
  };
}

export const isSameDayTz = (date1, date2, timezone?: string) => {
  const d1 = dateToLocalTime(date1, timezone);
  const d2 = dateToLocalTime(date2, timezone);
  return isSameDay(d1, d2);
};

export const isTodayTz = (date, timezone?: string) => {
  const d = dateToLocalTime(date, timezone);
  const today = dateToLocalTime(new Date(), timezone);
  return isSameDay(d, today);
};

// Check if a date is in the past in organization timezone
export const isPastTz = (date) => {
  const d = dateToLocalTime(date);
  const now = dateToLocalTime(new Date());
  return isPast(d);
};

// Get end of day in organization timezone
export const endOfDayTz = (date) => {
  const d = dateToLocalTime(date);
  return endOfDay(d);
};
